/**
 * Biblioteca para gerenciar funcionalidade "Lembrar-me"
 * Usa tokens seguros armazenados em cookies HttpOnly
 */

import { supabase } from '@/integrations/supabase/client';
import Cookies from 'js-cookie';

const REMEMBER_ME_COOKIE = 'imobpanda_remember_token';
const REMEMBER_ME_DURATION = 30; // dias

interface DeviceInfo {
  userAgent: string;
  platform: string;
  language: string;
}

/**
 * Gera um token UUID v4 seguro
 */
function generateSecureToken(): string {
  return crypto.randomUUID();
}

/**
 * Obtém informações do dispositivo
 */
function getDeviceInfo(): DeviceInfo {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
  };
}

/**
 * Cria uma sessão "Lembrar-me" para o usuário
 */
export async function createRememberMeSession(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const token = generateSecureToken();
    const deviceInfo = getDeviceInfo();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + REMEMBER_ME_DURATION);

    // Salvar no banco de dados
    const { error } = await supabase
      .from('user_sessions')
      .insert({
        user_id: userId,
        token,
        device_info: deviceInfo,
        expires_at: expiresAt.toISOString(),
      });

    if (error) {
      console.error('[RememberMe] Erro ao criar sessão:', error);
      return { success: false, error: error.message };
    }

    // Salvar token em cookie seguro
    Cookies.set(REMEMBER_ME_COOKIE, token, {
      expires: REMEMBER_ME_DURATION,
      secure: window.location.protocol === 'https:',
      sameSite: 'strict',
    });

    return { success: true };
  } catch (error) {
    console.error('[RememberMe] Erro inesperado:', error);
    return { success: false, error: 'Erro ao criar sessão' };
  }
}

/**
 * Remove a sessão "Lembrar-me" atual
 */
export async function removeRememberMeSession(): Promise<void> {
  try {
    const token = Cookies.get(REMEMBER_ME_COOKIE);
    
    if (token) {
      // Remover do banco
      await supabase
        .from('user_sessions')
        .delete()
        .eq('token', token);
    }

    // Remover cookie
    Cookies.remove(REMEMBER_ME_COOKIE);
  } catch (error) {
    console.error('[RememberMe] Erro ao remover sessão:', error);
  }
}

/**
 * Remove todas as sessões do usuário (logout de todos os dispositivos)
 */
export async function logoutAllDevices(userId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('user_sessions')
      .delete()
      .eq('user_id', userId);

    if (error) {
      return { success: false, error: error.message };
    }

    // Remover cookie local
    Cookies.remove(REMEMBER_ME_COOKIE);

    return { success: true };
  } catch (error) {
    console.error('[RememberMe] Erro ao fazer logout de todos os dispositivos:', error);
    return { success: false, error: 'Erro ao fazer logout' };
  }
}
